import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, publishReplay, refCount } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';

import { AppMonitoringService } from '@core/services/app-monitoring.service';
import { MonitoringAttributes } from '@core/enums/monitoring-attributes.enum';
import { StorageService } from '@core/services/storage.service';

import { ApplicationUser } from '../DTO/applicationUser';
import { ApplicationUserModel } from '../models/application-user.model';
import { ApplicationUserConverter } from '../converters/application-user.converter';
import { ClientAccountService } from './client-account.service';
import { AppIdConstants } from '../constants/appId.constants';
import { OrderModel } from '../../orders/models/order.model';
import { CustomerModel } from '../models/customer.model';
import { AccountModel } from '../models/account.model';
import { ProductModel } from '../../orders/models/product.model';
import { FeaturesService } from './features.service';
import { ExternalRolesConstants, RolesLegalcertNovaConstants } from '../constants/roles.constants';
import { ConfigurationService } from './configuration.service';

/**
 * Users service
 * questa classe serve per gestire i ruoli che un utente ha
 */

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  cachedUsers: Observable<any>;
  private user: ApplicationUser;
  private _activeRole: string;
  public userChoiced = new BehaviorSubject(false);
  private _fromCallcenter: boolean;
  private users: ApplicationUserModel[];

  constructor(
    private httpClient: HttpClient,
    private applicationUserConverter: ApplicationUserConverter,
    private router: Router,
    private storageService: StorageService,
    private clientAccountService: ClientAccountService,
    private featuresService: FeaturesService,
    private configurationService: ConfigurationService,
    private monitoringService: AppMonitoringService
  ) {
    this.user = null;
  }

  private getBaseResourceEndpoint(): string {
    return this.configurationService.configuration.api.users;
  }

  // prende gli utenti da /eobi/users se non presenti in cache
  getUsers(): Observable<Array<ApplicationUserModel>> {
    if (!this.cachedUsers) {
      this.cachedUsers = this.httpClient
        .get(this.getBaseResourceEndpoint())
        .pipe(
          map((res: Array<ApplicationUser>) => {
            this.users = this.applicationUserConverter.convertToModelList(res);
            return this.users;
          }),
          publishReplay(1), // this tells Rx to cache the latest emitted
          refCount()
          // and this tells Rx to keep the Observable alive as long as there are any Subscribers
        );
    }
    return this.cachedUsers;
  }

  getUser(): ApplicationUserModel {
    return (
      <ApplicationUserModel>this.user ||
      (this.storageService.getPreference('user')
        ? this.applicationUserConverter.convertUserFormStorage(
            this.storageService.getPreference('user')
          )
        : null) ||
      null
    );
  }

  setUser(user: ApplicationUser) {
    this.user = user;
    this.monitoringService.addAttributeToCurrentSession(
      MonitoringAttributes.USER,
      user.identityId
    );
    this.storageService.setPreference('user', user);
    this.userChoiced.next(true);
  }

  changeUser() {
    this.router.navigate(['user-choice']);
    this.clearUser();
    this.clientAccountService.clearUnit();
  }

  clearUser() {
    this.user = null;
    this.activeRole = null;
    this.monitoringService.clearAttributeFromCurrentSession(
      MonitoringAttributes.USER
    );
    this.storageService.deletePreference('user');
    this.storageService.deletePreference('role');
    this.featuresService.clearFeatures();
    this.userChoiced.next(false);
  }

  setDefaultRole() {
    for (let role of this.user?.roles) {
      if (role === 'OPV' || role === 'ORS') {
        this._activeRole = role;
      }
      if (this.user.appId === AppIdConstants.SAC_CALLCENTER) {
        this._activeRole = 'OCC';
      }
    }

    if (!this._activeRole) {
      this._activeRole = this.user?.roles[0];
    }
    this.storageService.setPreference('role', this._activeRole);
  }

  get activeRole(): string {
    return this._activeRole;
  }

  set activeRole(value: string) {
    this._activeRole = value;
    this.storageService.setPreference('role', this._activeRole);
  }

  isCallCenterUser(): boolean {
    return this.getUser()?.appId === AppIdConstants.SAC_CALLCENTER;
  }

  isRaoUser(): boolean {
    if (!this.users) {
      return false;
    }
    return this.users.some(
      (u) =>
        u.external &&
        u.aggregations.some((r) =>
          r.roles.some((role) => role._id === ExternalRolesConstants.RAO)
        )
    );
  }

  isIrUser(): boolean {
    if (!this.users) {
      return false;
    }
    return this.users.some(
      (u) =>
        u.external &&
        u.aggregations.some((r) =>
          r.roles.filter((role) => role._id === ExternalRolesConstants.IR)
        )
    );
  }

  isLegalcertNovaAdmin(): boolean {
    if (!this.users) {
      return false;
    }
    const appid = this.users.find((u) => u.appId === AppIdConstants.LEGALCERT_NOVA);
    if (appid) {
      return (appid.roles.find((r) => r === RolesLegalcertNovaConstants.ADMIN) ? true : false);
    }
    return false;
  }

  identify(model: OrderModel | ProductModel): Observable<void> {
    return new Observable((subscriber) => {
      let customerNew: CustomerModel = new CustomerModel();
      let accountNew: AccountModel = new AccountModel();

      this.clientAccountService.getClients().subscribe((customers) => {
        for (let customer of customers) {
          if (customer.customerCode === model.customerCode) {
            customerNew = customer;
          }
        }

        this.clientAccountService
          .getAccounts(model.customerCode)
          .subscribe((accounts) => {
            for (let account of accounts) {
              if (account.accountCode === model.accountCode) {
                accountNew = account;
              }
            }

            this.clientAccountService.clearUnit(true);
            this.clearUser();

            this.getUsers().subscribe((users: Array<ApplicationUserModel>) => {
              for (let user of users) {
                if (user.appId === AppIdConstants.SAC_RES) {
                  this.setUser(user);
                  this.setDefaultRole();
                  this.clientAccountService.setCustomer(customerNew);
                  this.clientAccountService.setAccount(accountNew);
                  if (model instanceof OrderModel) {
                    this.router.navigate([
                      'search',
                      'detail-order',
                      model.orderId,
                    ]);
                  }
                  if (model instanceof ProductModel) {
                    this.router.navigate([
                      'search',
                      'detail-product',
                      model.userId,
                    ]);
                  }
                  this.fromCallcenter = true;
                  this.storageService.setPreference(
                    'fromCc',
                    this.fromCallcenter
                  );
                  subscriber.next();
                }
              }
            });
          });
      });
    });
  }

  returnToCc(model: OrderModel | ProductModel): Observable<void> {
    return new Observable((subscriber) => {
      this.clientAccountService.clearUnit(true);
      this.clearUser();

      this.getUsers().subscribe((users: Array<ApplicationUserModel>) => {
        for (let user of users) {
          if (user.appId === AppIdConstants.SAC_CALLCENTER) {
            this.setUser(user);
            this.setDefaultRole();
            this.clientAccountService.setCustomer(null);
            this.clientAccountService.setAccount(null);
            if (model instanceof OrderModel) {
              this.router.navigate(['search', 'detail-order', model.orderId]);
            }
            if (model instanceof ProductModel) {
              this.router.navigate(['search', 'detail-product', model.userId]);
            }
            this.fromCallcenter = false;
            this.storageService.deletePreference('fromCc');
            subscriber.next();
          }
        }
      });
    });
  }

  get fromCallcenter(): boolean {
    return this._fromCallcenter || this.storageService.getPreference('fromCc');
  }

  set fromCallcenter(value: boolean) {
    this._fromCallcenter = value;
  }
}
